import { Button } from 'antd'
import { observer } from 'mobx-react-lite'
import { useEffect, useState } from 'react'
import { IoHomeOutline } from 'react-icons/io5'

import { userManager } from '@/Core/Managers'
import { ResolveDelayModal } from '@/Ship/Components/Modals/ResolveDelayModal'
import { DispatcherFilterSelect } from '@/Ship/Components/DispatcherFilterSelect'
import { RefreshButton } from '@/Ship/Components/UI/RefreshButton/RefreshButton'
import { cx } from '@/Ship/Helpers/cssClass'
import type { DelayRow } from '@/Ship/Contracts/DashboardSummary'
import { REQUEST_STATUS_MAP, type RequestStatus } from '@/Ship/Contracts/Statuses/RequestStatus'
import { TASK_STATUS_MAP, type TaskStatus } from '@/Ship/Contracts/Statuses/TaskStatus'
import { DashboardStore } from '@/Ship/Stores/DashboardStore'
import listStyles from '@/Ship/Templates/CrudResource/CrudResourceTemplate.module.css'

import { ActiveDelays } from '../Components/ActiveDelays'
import { AttentionTiles } from '../Components/AttentionTiles'
import { Funnel, type FunnelStage } from '../Components/Funnel'
import { PerformancePanel } from '../Components/PerformancePanel'
import {
    requestsUrl,
    TASK_SOURCE_LABELS,
    tasksUrl,
    type LinkDispatcher,
    type TaskSource,
} from '../Helpers/dashboardLinks'

import styles from '../Components/Dashboard.module.css'

import type { IPageConfig } from '@/Core/Contracts/Loaders/IPageConfig'

/** Як часто оновлювати головну, поки вкладка активна */
const REFRESH_INTERVAL_MS = 60_000

const REQUEST_STAGES: RequestStatus[] = ['new', 'in_work', 'transport_distributed']

const TASK_STAGES: TaskStatus[] = [
    'new',
    'assigned_executor',
    'execution_started',
    'loading_started',
    'arrived_at_unloading',
    'unloading_started',
    'completed_by_driver',
]

/**
 * Автооновлення: раз на хвилину, лише коли вкладка видима; при поверненні на вкладку —
 * одразу, якщо дані встигли застаріти.
 */
const useAutoRefresh = (store: DashboardStore) => {
    useEffect(() => {
        const isStale = () => !store.updatedAt || Date.now() - store.updatedAt.getTime() >= REFRESH_INTERVAL_MS
        const tick = () => {
            if (document.visibilityState === 'visible' && !store.refreshing) void store.refresh()
        }
        const onVisibility = () => {
            if (document.visibilityState === 'visible' && isStale() && !store.refreshing) void store.refresh()
        }
        const timer = window.setInterval(tick, REFRESH_INTERVAL_MS)
        document.addEventListener('visibilitychange', onVisibility)
        return () => {
            window.clearInterval(timer)
            document.removeEventListener('visibilitychange', onVisibility)
        }
    }, [store])
}

/**
 * Головна — робоче місце диспетчера: що потребує дії зараз, де задачі й заявки у воронці,
 * показники за період з динамікою і активні затримки. Один фільтр «Відповідальний
 * диспетчер» на всю сторінку (звичайний диспетчер бачить лише свої дані без фільтра);
 * переходи ведуть у списки з тим самим фільтром, тож числа збігаються.
 */
const IndexPage = observer(() => {
    const [store] = useState(() => new DashboardStore())
    const [resolvingDelay, setResolvingDelay] = useState<DelayRow | null>(null)

    useEffect(() => {
        void store.refresh()
    }, [store])
    useAutoRefresh(store)

    // Ролі — без мемоізації: перевірка дешева, а userManager може догідруватися після першого рендера
    const hasRole = (roles: string[]) => userManager.hasAnyRole?.(roles) ?? false
    const canResolve = hasRole(['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_DISPATCHER', 'ROLE_SENIOR_DISPATCHER'])
    const canSeeReports = hasRole(['ROLE_ADMIN', 'ROLE_SUPER_ADMIN', 'ROLE_SENIOR_DISPATCHER'])
    // Чи побачить користувач лише своє (вирішує бекенд), передбачаємо за ролями, щоб фільтр
    // диспетчера був у шапці з першого кадру, а не з'являвся після завантаження, зсуваючи кнопку
    const likelyScoped =
        hasRole(['ROLE_DISPATCHER']) && !hasRole(['ROLE_SENIOR_DISPATCHER', 'ROLE_ADMIN', 'ROLE_SUPER_ADMIN'])
    const showDispatcherFilter = store.summary ? !store.isScopedDispatcher : !likelyScoped

    // Дані іншого диспетчера (запит ще йде або впав) не показуємо під новим підписом
    const summary = store.summaryFresh ? store.summary : null
    const summaryLoading = !summary && store.summaryLoading

    // Диспетчер для переходів у списки: сам користувач (бачить лише своє) або вибраний
    const user = userManager.user as Record<string, any>
    const linkDispatcher: LinkDispatcher | null = store.isScopedDispatcher
        ? typeof user?.['uuid'] === 'string'
            ? { uuid: user['uuid'], name: user['shortName'] || user['fullName'] || 'Я' }
            : null
        : store.dispatcher?.uuid
          ? { uuid: store.dispatcher.uuid, name: store.dispatcher.label }
          : null

    const requestStages: FunnelStage[] = REQUEST_STAGES.map((status) => {
        const count = summary?.requests[status] ?? 0
        return {
            key: status,
            label: REQUEST_STATUS_MAP[status].label,
            color: REQUEST_STATUS_MAP[status].color,
            count,
            links: [{ key: 'list', label: 'Відкрити →', to: requestsUrl(status, linkDispatcher) }],
        }
    })

    const taskStages: FunnelStage[] = TASK_STAGES.map((status) => {
        const split = summary?.assignments[status] ?? { total: 0, requests: 0, standalone: 0 }
        return {
            key: status,
            label: TASK_STATUS_MAP[status].label,
            color: TASK_STATUS_MAP[status].color,
            count: split.total,
            links: (['requests', 'standalone'] as TaskSource[])
                .filter((source) => split[source] > 0)
                .map((source) => ({
                    key: source,
                    label: `${TASK_SOURCE_LABELS[source]} ${split[source]}`,
                    to: tasksUrl(source, { status }, linkDispatcher),
                })),
        }
    })

    const scopeText = store.isScopedDispatcher
        ? 'Ваші заявки, задачі й затримки'
        : store.dispatcher
          ? `Дані диспетчера: ${store.dispatcher.label}`
          : 'Дані по всій системі'
    const updatedText = store.updatedAt
        ? `Оновлено о ${store.updatedAt.toLocaleTimeString('uk-UA', { hour: '2-digit', minute: '2-digit' })}`
        : 'Завантаження…'

    return (
        <div className={cx(listStyles, 'page')}>
            <div className={cx(styles, 'page')}>
                <header className={cx(styles, 'pageHeader')}>
                    <div>
                        <h1 className={cx(styles, 'pageTitle')}>
                            <span className={cx(styles, 'pageIcon')}>
                                <IoHomeOutline />
                            </span>
                            Головна
                        </h1>
                        <p className={cx(styles, 'pageSubtitle')}>
                            {scopeText} · {updatedText}
                            {store.summaryError && summary && (
                                <span className={cx(styles, 'subtitleError')}> · не вдалося оновити</span>
                            )}
                        </p>
                    </div>
                    <div className={cx(styles, 'pageActions')}>
                        {showDispatcherFilter && (
                            <DispatcherFilterSelect
                                value={store.dispatcher?.value ?? null}
                                onChange={(value) =>
                                    store.setDispatcher(
                                        value === null
                                            ? null
                                            : (store.dispatcherOptions.find((option) => option.value === value) ?? null)
                                    )
                                }
                                options={store.dispatcherOptions}
                                loading={store.dispatchersLoading}
                                onSearch={(search) => void store.fetchDispatchers(search)}
                                placeholder="Усі диспетчери"
                                minWidth={200}
                            />
                        )}
                        <RefreshButton onRefresh={() => void store.refresh()} refreshing={store.refreshing} />
                    </div>
                </header>

                {store.summaryError && !summary && !store.summaryLoading ? (
                    <section className={cx(listStyles, 'card')}>
                        <div className={cx(styles, 'empty')} role="alert">
                            {store.summaryError}
                            <Button
                                size="small"
                                className={cx(styles, 'emptyAction')}
                                onClick={() => void store.refresh()}
                            >
                                Повторити
                            </Button>
                        </div>
                    </section>
                ) : (
                    <>
                        <AttentionTiles
                            attention={summary?.attention ?? null}
                            loading={summaryLoading}
                            dispatcher={linkDispatcher}
                        />

                        {/* Широкий екран: воронки й показники ліворуч, затримки — колонкою праворуч;
                            вужче — одна колонка, затримки одразу після «Потребує уваги». Затримки
                            першими в DOM, щоб порядок фокуса збігався з візуальним на вузьких екранах */}
                        <div className={cx(styles, 'body')}>
                            <div className={cx(styles, 'aside')}>
                                <ActiveDelays
                                    delays={summary?.activeDelays ?? []}
                                    loading={summaryLoading}
                                    canResolve={canResolve}
                                    onResolve={setResolvingDelay}
                                />
                            </div>

                            <div className={cx(styles, 'main')}>
                                <div className={cx(styles, 'funnels')}>
                                    <section
                                        className={cx(listStyles, 'card')}
                                        aria-labelledby="dashboard-requests-title"
                                    >
                                        <header className={cx(listStyles, 'header')}>
                                            <h2 id="dashboard-requests-title" className={cx(styles, 'sectionTitle')}>
                                                Заявки в роботі
                                            </h2>
                                            <span className={cx(styles, 'sectionHint')}>до розподілу транспорту</span>
                                        </header>
                                        <Funnel
                                            stages={requestStages}
                                            loading={summaryLoading}
                                            ariaLabel="Заявки за етапами"
                                        />
                                    </section>
                                    <section className={cx(listStyles, 'card')} aria-labelledby="dashboard-tasks-title">
                                        <header className={cx(listStyles, 'header')}>
                                            <h2 id="dashboard-tasks-title" className={cx(styles, 'sectionTitle')}>
                                                Задачі в роботі
                                            </h2>
                                            <span className={cx(styles, 'sectionHint')}>
                                                до підтвердження диспетчером
                                            </span>
                                        </header>
                                        <Funnel
                                            stages={taskStages}
                                            loading={summaryLoading}
                                            ariaLabel="Задачі за етапами"
                                        />
                                    </section>
                                </div>

                                <PerformancePanel
                                    performance={store.performanceFresh ? store.performance : null}
                                    trend={store.trendFresh ? (store.performance?.trend ?? null) : null}
                                    period={store.period}
                                    onPeriodChange={(period) => store.setPeriod(period)}
                                    loading={!store.performanceFresh && store.performanceLoading}
                                    error={store.performanceError}
                                    onRetry={() => void store.fetchPerformance()}
                                    showReportLink={canSeeReports}
                                />
                            </div>
                        </div>
                    </>
                )}
            </div>

            <ResolveDelayModal
                delay={resolvingDelay}
                open={resolvingDelay !== null}
                onClose={() => setResolvingDelay(null)}
                onResolved={() => void store.fetchSummary()}
            />
        </div>
    )
})

export default IndexPage

export const pageConfig: IPageConfig = {
    // Явний маршрут '/': сторінка живе в контейнері, і ViteKernelLoaderPlugin інакше вивів би
    // '/index' зі шляху секції (для контейнерів '/' автоматично не підставляється)
    route: '/',
    title: 'Головна',
    icon: <IoHomeOutline />,
}
